import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { Divider, IconButton, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText } from "@mui/material";
import { mmRfqFormDataSelector } from "../../Redux/MMRfq/Selector";
import { connect, useDispatch } from "react-redux";
import { createStructuredSelector } from "reselect";
import { setMmRfqFormData } from "../../Redux/MMRfq/actions";
import { useFileContext } from "./FileContext";

const AddDocuments = ({ MM_RFQ_FORM_DATA }) => {
    const { addFile, removeFile, getFile } = useFileContext();

    const dispatch = useDispatch();
    const [fileTec, setFileTec] = useState<File | null>(
        MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.Upload_Technical_File || null
    );
    const fileInputRefTec = useRef<HTMLInputElement | null>(null);
    const [errorTextTec, setErrorTextTec] = useState<string>("");
    const [compliance, setCompliance] = useState(
        MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.compliance
    );
    const [qualityStandard, setQualityStandard] = useState(
        MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.quality_standard
    );
    const [comments, setComments] = useState(
        MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.comments
    );
    const [error, setError] = useState(false);

    const [complianceError, setComplianceError] = useState(false);
    const [qualityStandardError, setQualityStandardError] = useState(false);
    const [commentsError, setCommentsError] = useState(false);

    const preventDefaultTec = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleDropTec = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const droppedFile = event.dataTransfer.files?.[0];
        if (!droppedFile) return;
        if (!isValidFile(droppedFile)) {
            setErrorTextTec("Only PDF files under 10MB are allowed.");
            setFileTec(null);
        } else {
            setErrorTextTec("");
            addFile("Upload_Technical_Details", droppedFile);
            setFileTec(droppedFile);
        }
    };
    const handleFileChangeTec = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const selectedFile = event.target.files?.[0];
        if (fileInputRefTec.current) {
            fileInputRefTec.current.value = "";
        }
        if (!selectedFile) return;
        if (!isValidFile(selectedFile)) {
            setErrorTextTec("Only PDF files under 10MB are allowed.");
            setFileTec(null);
        } else {
            setErrorTextTec("");
            setFileTec(selectedFile);
            addFile("Upload_Technical_Details", selectedFile);
        }
    };
    const handleClearFileTec = () => {
        setFileTec(null);
        if (fileInputRefTec.current) {
            fileInputRefTec.current.value = "";
            const updatedArray = JSON.parse(JSON.stringify(MM_RFQ_FORM_DATA)); // deep clone

            updatedArray[0].TechnicalDetails = {
                ...updatedArray[0].TechnicalDetails,
                Upload_Technical_File: ""
            };
            dispatch(setMmRfqFormData(updatedArray));
            removeFile("Upload_Technical_Details");
        }
    };
    const MAX_FILE_SIZE_MB = 10;
    const isValidFile = (file: File): boolean => {
        const allowedTypes = [
            "application/pdf",
            "application/x-rar-compressed",
            "application/zip",
            "application/x-zip-compressed",
            "multipart/x-zip"
        ];
        const isAllowedType = allowedTypes.includes(file.type);
        const isAllowedSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024; // 50MB in bytes
        return isAllowedType && isAllowedSize;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (input.length <= 1000) {
            setCompliance(input);
            setComplianceError(false);
            const updatedArray = [...MM_RFQ_FORM_DATA];
            updatedArray[0] = {
                ...updatedArray[0],
                TechnicalDetails: {
                    ...updatedArray[0].TechnicalDetails,
                    compliance: input
                }
            };
            if (MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.compliance !== input) {
                dispatch(setMmRfqFormData(updatedArray));
            }
        } else {
            setComplianceError(true);
        }
    };

    const handleChangeComments = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (input.length <= 1000) {
            setComments(input);
            setCommentsError(false);
            const updatedArray = [...MM_RFQ_FORM_DATA];
            updatedArray[0] = {
                ...updatedArray[0],
                TechnicalDetails: {
                    ...updatedArray[0].TechnicalDetails,
                    comments: input
                }
            };

            if (MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.comments !== input) {
                dispatch(setMmRfqFormData(updatedArray));
            }
        } else {
            setCommentsError(true);
        }
    };
    const handleChangeQualityStandars = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = e.target.value;
        if (input.length <= 1000) {
            setQualityStandard(input);
            setQualityStandardError(false);
            const updatedArray = [...MM_RFQ_FORM_DATA];
            updatedArray[0] = {
                ...updatedArray[0],
                TechnicalDetails: {
                    ...updatedArray[0].TechnicalDetails,
                    quality_standard: input
                }
            };

            if (
                MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.quality_standard !==
                input
            ) {
                dispatch(setMmRfqFormData(updatedArray));
            }
        } else {
            setQualityStandardError(true);
        }
    };
    useEffect(() => {
        if (!fileTec) return;

        const metadata = {
            name: fileTec.name,
            size: fileTec.size,
            type: fileTec.type,
            lastModified: fileTec.lastModified // use this instead of entire File object
        };

        const updatedArray = JSON.parse(JSON.stringify(MM_RFQ_FORM_DATA)); // deep clone

        updatedArray[0].TechnicalDetails = {
            ...updatedArray[0].TechnicalDetails,
            Upload_Technical_File: metadata
        };

        dispatch(setMmRfqFormData(updatedArray));
    }, [fileTec]);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" mb={1}>
                Add Documents & Comments
            </Typography>
            <Typography sx={{ color: "text.secondary" }} variant="body2" mb={2}>
                Please add any additional details for the internal RFQ to ensure
                precise communication with partners. These details could include
                technical specifications, delivery instructions, or any other
                special requirements
            </Typography>
            <Typography variant="body1" mb={0}>
                Technical Details
            </Typography>
            <Grid container spacing={1} my={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" my={1}>
                        Upload Technical Details
                    </Typography>
                    <label
                        htmlFor="fileInput"
                        style={{ width: "100%", cursor: "pointer" }}
                    >
                        <Box
                            sx={{
                                border: "1px dashed #ccc",
                                borderRadius: "8px",
                                textAlign: "center",
                                padding: "0.5rem 0.5rem 0.7rem",
                                backgroundColor: "#FAFAFA",
                                "&:hover": {
                                    borderColor: "#1976D2"
                                }
                            }}
                            onDrop={handleDropTec}
                            onDragOver={preventDefaultTec}
                            onDragEnter={preventDefaultTec}
                        >
                            <CloudUploadIcon
                                sx={{ fontSize: "2.5rem", color: "#1976D2" }}
                            />
                            <Typography variant="body1" gutterBottom>
                                Upload or drag and drop
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                PDF (max. 10MB)
                            </Typography>
                            <input
                                type="file"
                                id="fileInput"
                                style={{ display: "none" }}
                                ref={fileInputRefTec}
                                onChange={handleFileChangeTec}
                                accept=".pdf"
                            />
                        </Box>
                    </label>
                    {fileTec && (
                        <Box mt={1}>
                            <Typography variant="body2">
                                File name: {fileTec.name}
                                <IconButton
                                    color="error"
                                    aria-label="remove file"
                                    onClick={handleClearFileTec}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Typography>
                        </Box>
                    )}
                    {/* {errorTextTec && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errorTextTec}
            </FormHelperText>
          )} */}
                    {/* {MM_RFQ_FORM_DATA[0]?.TechnicalDetails?.Upload_Technical_File <= 0 &&
            MM_RFQ_FORM_DATA[0]?.isNextButtomClicked && (
              <FormHelperText error sx={{ mt: 1 }}>
                Technical File Required
              </FormHelperText>
            )} */}
                </Grid>
            </Grid>
            <Grid container spacing={1} my={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        id="outlined-multiline-static"
                        label="Compliance Requirements"
                        multiline
                        fullWidth
                        rows={3}
                        placeholder="Enter Compliance Requirements"
                        value={compliance}
                        onChange={handleChange}
                        error={complianceError}
                        helperText={
                            complianceError ? "Max 1000 characters" : ""
                        }
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ paddingLeft: "10px" }}>
                    <TextField
                        id="outlined-multiline-static"
                        label="Quality Standards"
                        placeholder="Enter Quality Standards"
                        multiline
                        fullWidth
                        rows={3}
                        value={qualityStandard}
                        onChange={handleChangeQualityStandars}
                        error={qualityStandardError}
                        helperText={
                            qualityStandardError ? "Max 1000 characters" : ""
                        }
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1} my={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        id="outlined-multiline-static"
                        label="Comments"
                        multiline
                        fullWidth
                        rows={2}
                        placeholder="Add Comments"
                        value={comments}
                        onChange={handleChangeComments}
                        error={commentsError}
                        helperText={commentsError ? "Max 1000 characters" : ""}
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    MM_RFQ_FORM_DATA: mmRfqFormDataSelector
});

export default connect(mapStateToProps)(AddDocuments);
